import { Filter } from '../models/filter.model';
import { SingleValueFilterOption } from '../models/singlevaluefilteroption.model';
import { FilterOption } from '../interfaces/filteroption.interface';
import { RangeFilterOption } from '../models/rangefilteroption.model';
import { Injectable } from '@angular/core';
import { assertIsDefined } from '../utils/assert.utils';

@Injectable()
export class FilterHelper {
  /**
   * Input a set of items, and output a subset matching a set of filters
   * @param items
   * @param filters
   * @returns {any}
   */
  filter<T extends object>(items: T[], filters: Filter[]): T[] {
    // Short-circuit for when no filters are provided
    if (!filters || Object.keys(filters).length === 0) {
      return items;
    }

    // for each item, match it with the active filters
    return items.filter((item) => {
      return this.matchFilters(item, filters);
    });
  }

  /**
   * Returns an array of booleans with filter matches
   * @param item
   * @param filters
   * @returns {*[]}
   */
  matchFilters<T extends object>(item: T, filters: Filter[]): boolean {
    // for each filter in array, return true if matched, false if not

    return filters.every((filter: Filter) => {
      if (!filter.options.length) {
        return true;
      }
      const activeFilterOptions = filter.options.filter(
        (option: FilterOption) => {
          return option.active;
        },
      );
      if (!activeFilterOptions.length) {
        return true;
      }
      return activeFilterOptions.some((option: FilterOption) => {
        if (option instanceof SingleValueFilterOption) {
          return this.matchSingleValueFilterOption(filter.name, option, item);
        } else if (option instanceof RangeFilterOption) {
          return this.matchRangeFilterOption(filter.name, option, item);
        } else {
          throw new TypeError('Unsupported Filter option type: ' + option);
        }
      });
    });
  }

  public matchSingleValueFilterOption<T extends object>(
    propertyToCheck: string,
    option: SingleValueFilterOption,
    item: T,
  ): boolean {
    // Check if the property exists
    if (propertyToCheck in item) {
      const value: T[keyof T] = item[<keyof T>propertyToCheck];
      const valueToMatch = option.valueToMatch;
      if (value instanceof Array) {
        return value.indexOf(valueToMatch) !== -1;
      } else {
        return value === valueToMatch;
      }
    }
    return false;
  }

  public matchRangeFilterOption<T extends object>(
    propertyToCheck: string,
    option: RangeFilterOption,
    item: T,
  ): boolean {
    // Check if the property exists
    if (propertyToCheck in item) {
      const value: T[keyof T] = item[<keyof T>propertyToCheck];
      if (value instanceof Array) {
        return value.some((aValue: number) => {
          return this.matches(option, aValue);
        });
      } else {
        return this.matches(option, <number>value);
      }
    }
    return false;
  }

  public matches(option: RangeFilterOption, value: number): boolean {
    if (option.rangeStart === undefined && option.rangeEnd === undefined) {
      return true;
    } else if (option.rangeStart === undefined) {
      assertIsDefined(option.rangeEnd);
      return value < option.rangeEnd;
    } else if (option.rangeEnd === undefined) {
      return option.rangeStart <= value;
    } else {
      return option.rangeStart <= value && value < option.rangeEnd;
    }
  }
}
